<template>
  <div :id="id || undefined" class="dropdown-container w-full">
    <label
      v-if="label"
      :for="id"
      class="block mb-2 text-sm font-medium text-gray-900"
      :class="{ 'text-red-700': validation }"
    >
      {{ label }}
    </label>
    <div class="relative">
      <div
        ref="dropdownContainer"
        class="w-full text-gray-900 border border-gray-300 rounded-lg bg-gray-50 p-2.5 text-sm flex items-center justify-between cursor-pointer"
        :class="{
          'ring-2 ring-blue-500 border-blue-500': isOpen,
          'focus:ring-blue-500 focus:border-blue-500': !isOpen,
          'dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500': true,
          '!bg-white': inline && !validation,
          'bg-red-50 border-red-500 text-red-900 placeholder-red-700 focus:ring-red-500 focus:border-red-500 dark:text-red-500 dark:placeholder-red-500 dark:border-red-500':
            validation
        }"
        tabindex="0"
        @click="toggleDropdown"
      >
        <div
          v-if="multiselect && selectedValues.length > 0"
          class="flex flex-wrap gap-1"
        >
          <span
            v-for="value in selectedValues"
            :key="value"
            class="inline-flex items-center px-2 py-0.5 bg-gray-200 rounded dark:bg-gray-600"
          >
            <span
              class="text-xs font-medium text-gray-600 dark:text-gray-300"
              >{{ getOptionLabel(value) }}</span
            >
            <button
              type="button"
              class="inline-flex pl-1 pt-0.5 items-center ms-1 text-sm bg-transparent rounded-xs"
              aria-label="Remove"
              @click.stop="removeValue(value)"
            >
              <svg
                v-if="size === 'lg'"
                xmlns="http://www.w3.org/2000/svg"
                width="10"
                height="10"
                viewBox="0 0 10 10"
                fill="none"
              >
                <path
                  stroke="currentColor"
                  stroke-width=".2"
                  d="M5.90741 5L9.30409 1.60332C9.36538 1.54412 9.41427 1.47331 9.4479 1.39502..."
                />
              </svg>
              <svg
                v-else
                class="w-2 h-2"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 14 14"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
                />
              </svg>
              <span class="sr-only">Remove badge</span>
            </button>
          </span>
        </div>

        <span v-else-if="!multiselect && selectedOption">
          <slot name="selected" :option="selectedOption">
            {{ selectedOption.label }}
          </slot>
        </span>
        <span v-else class="text-gray-500 dark:text-gray-400">{{
          placeholder
        }}</span>
        <div class="cursor-pointer relative z-10 pointer-events-none">
          <svg
            v-if="!isOpen"
            class="w-4 h-4 ml-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            ></path>
          </svg>

          <svg
            v-if="isOpen"
            class="w-4 h-4 ml-2"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M5 15l7-7 7 7"
            ></path>
          </svg>
        </div>
      </div>
      <component
        :is="portalComponent"
        v-bind="bodyRender ? { to: 'body' } : {}"
      >
        <div
          v-if="isOpen"
          ref="optionsContainer"
          class="absolute left-0 right-0 z-[200] mt-1 bg-white border text-gray-900 border-gray-300 rounded-lg shadow-lg dark:bg-gray-700 dark:border-gray-600"
          :style="selectOptionsStyle"
        >
          <div
            v-if="filter"
            class="p-2 border-b border-gray-200 dark:border-gray-600"
          >
            <input
              id="select-options-filter-input"
              v-model="searchQuery"
              type="text"
              :placeholder="filterPlaceholder"
              class="w-full p-2 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500 dark:placeholder-gray-400 dark:text-white"
              @click="(e) => e.target.focus()"
            />
          </div>
          <div
            class="overflow-y-auto"
            :class="optionsClass"
            :style="{ maxHeight: maxHeight ? `${maxHeight}px` : '240px' }"
          >
            <!-- <div
              v-if="recentSelections.length > 0 && !multiselect"
              class="px-4 py-1 text-xs text-gray-500 dark:text-gray-400"
            >
              Recent
            </div> -->

            <div
              v-for="(option, index) in displayOptions"
              :key="option.value"
              :class="{
                'rounded-t-lg': index === 0,
                'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600':
                  !isOptionDisabled(option.value),
                'opacity-50 cursor-not-allowed': isOptionDisabled(option.value)
              }"
            >
              <div
                v-if="multiselect"
                class="px-4 py-2 pt-3 flex items-center"
                @click.stop="
                  toggleOption(option, !isOptionSelected(option.value))
                "
              >
                <div class="w-4 h-4 relative flex items-center justify-center">
                  <input
                    :id="`checkbox-${option.value}`"
                    type="checkbox"
                    :checked="isOptionSelected(option.value)"
                    :disabled="isOptionDisabled(option.value)"
                    class="w-4 h-4 text-blue-600 bg-gray-100 border border-gray-300 rounded pointer-events-none focus:ring-0 dark:bg-gray-700 dark:border-gray-600 checked:bg-blue-600 checked:border-blue-600 dark:checked:bg-blue-600 dark:checked:border-blue-600"
                  />
                  <svg
                    v-if="isOptionSelected(option.value)"
                    class="w-3 h-3 text-white absolute pointer-events-none"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </div>
                <label
                  class="w-full ml-2 mt-2 text-sm font-medium text-gray-900 dark:text-gray-300"
                  >{{ option.label }}</label
                >
              </div>

              <div
                v-else
                class="px-4 py-2 w-full flex items-center justify-between"
                :class="{
                  'font-bold': option.value === modelValue && modelValue,
                  'text-black': option.value === modelValue && modelValue,
                  'font-normal': option.value !== modelValue && modelValue,
                  'text-gray-700 dark:text-gray-300':
                    option.value !== modelValue && modelValue
                }"
                @click="selectOption(option)"
              >
                <slot name="option" :option="option">
                  <span>{{ option.label }}</span>
                </slot>
                <svg
                  v-if="option.value === modelValue && modelValue"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="black"
                >
                  <path
                    fill-rule="evenodd"
                    d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
              </div>
            </div>
          </div>

          <div
            v-if="showNewItem"
            class="border-t mt-.75 border-gray-200 dark:border-gray-600"
          ></div>

          <div
            v-if="showNewItem"
            class="px-4 py-2.5 border-t dark:border-gray-600 text-blue-600 dark:text-blue-500 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
            @mousedown.stop.prevent="addNewItem"
          >
            + Add New Item
          </div>
        </div>
      </component>
    </div>
    <p v-if="helperText" :id="`${id}-helper-text`" class="text-red-600 mt-2">
      <span v-if="validation === 'success'" class="font-medium">Alright!</span>
      <span v-if="validation === 'error'" class="font-medium">Oops!</span>
      {{ helperText }}
    </p>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { SafeTeleport } from "vue-safe-teleport";

const props = defineProps({
  id: {
    type: String,
    default: ""
  },
  label: {
    type: String,
    default: ""
  },
  placeholder: {
    type: String,
    default: "Choose an option"
  },
  options: {
    type: Array,
    required: true
  },
  modelValue: {
    type: [String, Number, Array],
    default: null
  },
  isSelected: {
    type: Boolean,
    default: false
  },
  newItemText: {
    type: String,
    default: "Add"
  },
  multiselect: {
    type: Boolean,
    default: false
  },
  showNewItem: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: "md"
  },
  validation: {
    type: String,
    default: ""
  },
  helperText: {
    type: String,
    default: ""
  },
  bodyRender: {
    type: Boolean,
    default: true
  },
  optionsClass: {
    type: String,
    default: ""
  },
  filter: { type: Boolean, default: false },
  // Omit the leading empty "placeholder" row from single-select lists. Use for
  // required fields that always carry a value (e.g. a type picker) so the
  // dropdown doesn't open with a blank, selectable first row.
  hidePlaceholder: { type: Boolean, default: false },
  // White trigger background for controls placed on a tinted row, where the
  // default gray fill would blend in (mirrors InputField's `inline`).
  inline: { type: Boolean, default: false },
  filterPlaceholder: { type: String, default: "Filter options..." },
  maxSelections: { type: Number, default: null },
  maxHeight: { type: Number, default: null }
});

const emit = defineEmits(["update:modelValue", "add-item"]);

const isOpen = ref(false);
const dropdownContainer = ref(null);
const optionsContainer = ref(null);
const recentSelections = ref([]);
const selectedValues = ref([]);
const searchQuery = ref("");

const selectOptionsStyle = ref(null);

const positionOptions = () => {
  if (!props.bodyRender) return;
  if (!dropdownContainer.value) return;
  nextTick(() => {
    const dims = dropdownContainer.value.getBoundingClientRect();
    const { left, height, width } = dims;
    const MENU_OFFSET = 1;
    selectOptionsStyle.value = {
      left: `${left}px !important`,
      top: `${dims.top + height + window.pageYOffset + MENU_OFFSET}px`,
      width: `${width}px`,
      "z-index": "99999 !important"
    };
  });
};

const portalComponent = computed(() =>
  props.bodyRender ? SafeTeleport : "div"
);
watch(isOpen, (newValue, oldValue) => {
  if (newValue != oldValue && newValue) {
    positionOptions();
  }
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (props.multiselect && Array.isArray(newValue)) {
      selectedValues.value = [...newValue];
    } else if (props.multiselect && !Array.isArray(newValue)) {
      selectedValues.value = [];
      emit("update:modelValue", []);
    }
  },
  { immediate: true }
);

const closeDropdownOnClickOutside = (event) => {
  if (
    !dropdownContainer.value?.contains(event.target) &&
    !optionsContainer.value?.contains(event.target)
  ) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", closeDropdownOnClickOutside);
  window.addEventListener("resize", positionOptions);
});

onUnmounted(() => {
  document.removeEventListener("click", closeDropdownOnClickOutside);
  window.removeEventListener("resize", positionOptions);
});

const selectedOption = computed(() => {
  return displayOptions.value.find(
    (option) => option.value === props.modelValue
  );
});

const displayOptions = computed(() => {
  const options = searchQuery.value
    ? props.options.filter((opt) =>
        opt.label.toLowerCase().includes(searchQuery.value.toLowerCase())
      )
    : props.options;
  if (props.multiselect || props.hidePlaceholder) return options;
  return [{ label: props.placeholder, value: null }, ...options];
});

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value && dropdownContainer.value) {
    dropdownContainer.value.focus();
  }
};

const selectOption = (option) => {
  emit("update:modelValue", option.value);

  if (!recentSelections.value.find((item) => item.value === option.value)) {
    recentSelections.value.unshift(option);
    if (recentSelections.value.length > 5) {
      recentSelections.value.pop();
    }
  }

  isOpen.value = false;
};

const isAtMaxSelections = computed(() => {
  if (!props.maxSelections) return false;
  return selectedValues.value.length >= props.maxSelections;
});

const isOptionDisabled = (value) => {
  return isAtMaxSelections.value && !isOptionSelected(value);
};

const isOptionSelected = (value) => {
  if (!value) {
    return false;
  }
  return selectedValues.value.includes(value);
};

const toggleOption = (option, isChecked) => {
  const index = selectedValues.value.indexOf(option.value);

  if (isChecked && index === -1) {
    if (
      props.maxSelections &&
      selectedValues.value.length >= props.maxSelections
    ) {
      return;
    }
    selectedValues.value.push(option.value);
  } else if (!isChecked && index !== -1) {
    selectedValues.value.splice(index, 1);
  }

  // Emit updated array of values
  emit("update:modelValue", [...selectedValues.value]);
};

const removeValue = (value) => {
  const index = selectedValues.value.indexOf(value);
  if (index !== -1) {
    selectedValues.value.splice(index, 1);
    emit("update:modelValue", [...selectedValues.value]);
  }
};

const getOptionLabel = (value) => {
  const option = props.options.find((opt) => opt.value === value);
  return option ? option.label : value;
};

const addNewItem = () => {
  emit("add-item");
  isOpen.value = false;
};

watch(
  () => props.isSelected,
  (newValue) => {
    if (
      newValue &&
      props.options.length > 0 &&
      !props.modelValue &&
      !props.multiselect
    ) {
      emit("update:modelValue", props.options[0].value);
    }
  },
  { immediate: true }
);
</script>
<style scoped>
input[type="checkbox"]:focus,
input[type="checkbox"]:focus-visible {
  outline: none;
  box-shadow: none !important;
}
</style>
